import React, { useState } from 'react';
import { Card, Table, Button, Tag, Typography, Row, Col, Statistic, Modal, Form, Input, Select, Space, Progress, Tooltip } from 'antd';
import { CloudOutlined, SyncOutlined, EyeOutlined, DownloadOutlined, SettingOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface WeatherDataPoint {
    time: string;
    status: 'success' | 'warning' | 'error' | 'pending';
    lastUpdate?: string;
    dataSize?: number;
}

interface WeatherStation {
    id: string;
    station: string;
    lastUpdate: string;
    status: string;
    dataCount: number;
    dataDirectory?: string;
    weatherType?: string;
    dailyDataPoints?: WeatherDataPoint[];
}

const WeatherDataManagement: React.FC = () => {
    const [weatherData, setWeatherData] = useState<WeatherStation[]>([
        {
            id: '1',
            station: '风电场A-1号机组',
            lastUpdate: '2024-01-15 14:30',
            status: 'active',
            dataCount: 24,
            dataDirectory: '/data/weather/wind-farm-a1',
            weatherType: '盘古',
            dailyDataPoints: [
                { time: '02:00', status: 'success', lastUpdate: '2024-01-15 02:15', dataSize: 156 },
                { time: '08:00', status: 'success', lastUpdate: '2024-01-15 08:12', dataSize: 158 },
                { time: '14:00', status: 'success', lastUpdate: '2024-01-15 14:08', dataSize: 162 },
                { time: '20:00', status: 'pending', lastUpdate: '', dataSize: 0 },
            ]
        },
        {
            id: '2',
            station: '风电场A-2号机组',
            lastUpdate: '2024-01-15 14:25',
            status: 'active',
            dataCount: 24,
            dataDirectory: '/data/weather/wind-farm-a2',
            weatherType: '风谷',
            dailyDataPoints: [
                { time: '02:00', status: 'success', lastUpdate: '2024-01-15 02:18', dataSize: 148 },
                { time: '08:00', status: 'warning', lastUpdate: '2024-01-15 08:45', dataSize: 89 },
                { time: '14:00', status: 'success', lastUpdate: '2024-01-15 14:05', dataSize: 152 },
                { time: '20:00', status: 'pending', lastUpdate: '', dataSize: 0 },
            ]
        },
        {
            id: '3',
            station: '光伏场B-1号方阵',
            lastUpdate: '2024-01-15 14:20',
            status: 'active',
            dataCount: 22,
            dataDirectory: '/data/weather/solar-farm-b1',
            weatherType: 'EC',
            dailyDataPoints: [
                { time: '02:00', status: 'success', lastUpdate: '2024-01-15 02:22', dataSize: 142 },
                { time: '08:00', status: 'success', lastUpdate: '2024-01-15 08:18', dataSize: 145 },
                { time: '14:00', status: 'success', lastUpdate: '2024-01-15 14:15', dataSize: 148 },
                { time: '20:00', status: 'pending', lastUpdate: '', dataSize: 0 },
            ]
        },
        {
            id: '4',
            station: '水电站C-1号机组',
            lastUpdate: '2024-01-15 14:10',
            status: 'active',
            dataCount: 21,
            dataDirectory: '/data/weather/hydro-plant-c1',
            weatherType: 'GFS',
            dailyDataPoints: [
                { time: '02:00', status: 'success', lastUpdate: '2024-01-15 02:25', dataSize: 138 },
                { time: '08:00', status: 'success', lastUpdate: '2024-01-15 08:22', dataSize: 141 },
                { time: '14:00', status: 'warning', lastUpdate: '2024-01-15 14:35', dataSize: 95 },
                { time: '20:00', status: 'pending', lastUpdate: '', dataSize: 0 },
            ]
        },
    ]);

    const [isSettingModalVisible, setIsSettingModalVisible] = useState(false);
    const [currentStation, setCurrentStation] = useState<WeatherStation | null>(null);
    const [form] = Form.useForm();

    const weatherTypes = ['盘古', '风谷', 'EC', 'GFS', 'ECMWF'];

    const handleSettingClick = (station: WeatherStation) => {
        setCurrentStation(station);
        form.setFieldsValue({
            dataDirectory: station.dataDirectory,
            weatherType: station.weatherType,
        });
        setIsSettingModalVisible(true);
    };

    const handleSettingSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (currentStation) {
                setWeatherData(prev => prev.map(item =>
                    item.id === currentStation.id
                        ? { ...item, dataDirectory: values.dataDirectory, weatherType: values.weatherType }
                        : item
                ));
                setIsSettingModalVisible(false);
                setCurrentStation(null);
                form.resetFields();
            }
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    const handleSettingCancel = () => {
        setIsSettingModalVisible(false);
        setCurrentStation(null);
        form.resetFields();
    };

    const getWeatherTypeColor = (type: string) => {
        const colorMap: { [key: string]: string } = {
            '盘古': 'blue',
            '风谷': 'green',
            'EC': 'orange',
            'GFS': 'purple',
            'ECMWF': 'red',
        };
        return colorMap[type] || 'default';
    };

    const getDataPointIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'warning':
                return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
            case 'error':
                return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'pending':
                return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
            default:
                return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
        }
    };

    const getDataPointColor = (status: string) => {
        switch (status) {
            case 'success':
                return '#52c41a';
            case 'warning':
                return '#faad14';
            case 'error':
                return '#ff4d4f';
            case 'pending':
                return '#d9d9d9';
            default:
                return '#d9d9d9';
        }
    };

    const renderDataPointStatus = (dataPoints: WeatherDataPoint[]) => {
        return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {dataPoints.map((point, index) => (
                    <Tooltip
                        key={index}
                        title={
                            <div>
                                <div><strong>{point.time}</strong></div>
                                <div>状态: {point.status === 'success' ? '正常' : point.status === 'warning' ? '延迟' : point.status === 'error' ? '异常' : '等待中'}</div>
                                {point.lastUpdate && <div>更新时间: {point.lastUpdate}</div>}
                                {point.dataSize && point.dataSize > 0 && <div>数据大小: {point.dataSize}MB</div>}
                            </div>
                        }
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '4px',
                            border: `1px solid ${getDataPointColor(point.status)}`,
                            borderRadius: '4px',
                            backgroundColor: point.status === 'success' ? '#f6ffed' :
                                point.status === 'warning' ? '#fffbe6' :
                                    point.status === 'error' ? '#fff2f0' : '#fafafa'
                        }}>
                            {getDataPointIcon(point.status)}
                            <Text style={{ fontSize: '10px', marginTop: '2px' }}>{point.time}</Text>
                        </div>
                    </Tooltip>
                ))}
            </div>
        );
    };

    const calculateDailyProgress = (dataPoints: WeatherDataPoint[]) => {
        const completed = dataPoints.filter(p => p.status === 'success').length;
        return Math.round((completed / dataPoints.length) * 100);
    };

    // 计算总体统计数据
    const calculateOverallStats = () => {
        let totalPoints = 0;
        let successPoints = 0;
        let warningPoints = 0;
        let errorPoints = 0;

        weatherData.forEach(station => {
            if (station.dailyDataPoints) {
                station.dailyDataPoints.forEach(point => {
                    totalPoints++;
                    if (point.status === 'success') successPoints++;
                    else if (point.status === 'warning') warningPoints++;
                    else if (point.status === 'error') errorPoints++;
                });
            }
        });

        return { totalPoints, successPoints, warningPoints, errorPoints };
    };

    const stats = calculateOverallStats();

    const columns = [
        {
            title: '场站名称',
            dataIndex: 'station',
            key: 'station',
        },
        {
            title: '数据目录',
            dataIndex: 'dataDirectory',
            key: 'dataDirectory',
            render: (directory: string) => (
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {directory || '未设置'}
                </span>
            ),
        },
        {
            title: '气象类型',
            dataIndex: 'weatherType',
            key: 'weatherType',
            render: (type: string) => (
                <Tag color={getWeatherTypeColor(type)}>
                    {type || '未设置'}
                </Tag>
            ),
        },
        {
            title: '今日数据点状态',
            key: 'dailyDataPoints',
            render: (_: any, record: WeatherStation) => (
                <div>
                    {record.dailyDataPoints && renderDataPointStatus(record.dailyDataPoints)}
                    <div style={{ marginTop: '4px' }}>
                        <Progress
                            percent={record.dailyDataPoints ? calculateDailyProgress(record.dailyDataPoints) : 0}
                            size="small"
                            showInfo={false}
                            strokeColor={record.dailyDataPoints ? calculateDailyProgress(record.dailyDataPoints) === 100 ? '#52c41a' : '#1890ff' : '#d9d9d9'}
                        />
                        <Text style={{ fontSize: '10px', color: '#666' }}>
                            {record.dailyDataPoints ? calculateDailyProgress(record.dailyDataPoints) : 0}% 完成
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: '最后更新',
            dataIndex: 'lastUpdate',
            key: 'lastUpdate',
        },
        {
            title: '今日数据量',
            dataIndex: 'dataCount',
            key: 'dataCount',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? '正常' : '异常'}
                </Tag>
            ),
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: WeatherStation) => (
                <Space>
                    <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => handleSettingClick(record)}>
                        设置
                    </Button>
                    <Button type="link" size="small" icon={<SyncOutlined />}>
                        同步
                    </Button>
                    <Button type="link" size="small" icon={<EyeOutlined />}>
                        查看
                    </Button>
                    <Button type="link" size="small" icon={<DownloadOutlined />}>
                        下载
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-transition">
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col span={6}>
                    <Card>
                        <Statistic title="活跃场站" value="4 / 4" />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="今日数据点完成"
                            value={stats.successPoints}
                            suffix={`/ ${stats.totalPoints}`}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="数据异常点"
                            value={stats.errorPoints}
                            valueStyle={{ color: stats.errorPoints > 0 ? '#ff4d4f' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic title="最后同步" value="今天 14:30" />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col span={24}>
                    <Card title="今日数据点监控 (02:00, 08:00, 14:00, 20:00)" size="small">
                        <Row gutter={[16, 16]}>
                            {weatherData.map(station => (
                                <Col key={station.id} span={6}>
                                    <div style={{
                                        padding: '12px',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '6px',
                                        backgroundColor: '#fafafa'
                                    }}>
                                        <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                                            {station.station}
                                        </div>
                                        {station.dailyDataPoints && renderDataPointStatus(station.dailyDataPoints)}
                                        <div style={{ marginTop: '8px' }}>
                                            <Progress
                                                percent={calculateDailyProgress(station.dailyDataPoints || [])}
                                                size="small"
                                                strokeColor={calculateDailyProgress(station.dailyDataPoints || []) === 100 ? '#52c41a' : '#1890ff'}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>

            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title level={2} style={{ margin: 0 }}>
                        <CloudOutlined style={{ marginRight: '8px' }} />
                        气象数据管理
                    </Title>
                    <Button type="primary" icon={<SyncOutlined />}>
                        全部同步
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={weatherData}
                    rowKey="id"
                    pagination={false}
                />
            </Card>

            <Modal
                title={`设置 - ${currentStation?.station}`}
                open={isSettingModalVisible}
                onOk={handleSettingSubmit}
                onCancel={handleSettingCancel}
                width={600}
                okText="保存"
                cancelText="取消"
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: '20px' }}
                >
                    <Form.Item
                        label="气象数据目录"
                        name="dataDirectory"
                        rules={[
                            { required: true, message: '请输入气象数据目录路径' },
                            { pattern: /^\/.*/, message: '请输入有效的绝对路径（以/开头）' }
                        ]}
                    >
                        <Input
                            placeholder="请输入气象数据所在目录路径，如：/data/weather/station1"
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="气象类型"
                        name="weatherType"
                        rules={[{ required: true, message: '请选择气象类型' }]}
                    >
                        <Select placeholder="请选择气象数据类型">
                            {weatherTypes.map(type => (
                                <Option key={type} value={type}>
                                    <Tag color={getWeatherTypeColor(type)} style={{ marginRight: '8px' }}>
                                        {type}
                                    </Tag>
                                    {type}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginTop: '16px' }}>
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            <strong>说明：</strong><br />
                            • 数据目录：指定该场站数据文件的存储路径<br />
                            • 气象类型：选择对应的气象数据源类型，不同类型的数据格式和处理方式可能不同<br />
                            • 盘古：盘古气象模型数据<br />
                            • 风谷：风谷气象预报数据<br />
                            • EC：欧洲中期天气预报中心数据<br />
                            • 数据监控：系统会自动监控每日02:00、08:00、14:00、20:00四个时间点的数据接收状态
                        </Typography.Text>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default WeatherDataManagement; 